import React, { useEffect, useState } from "react"
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Link } from "react-router"
import { Dropdown } from 'react-bootstrap'
import Select from 'react-select'

import RightbarDI from 'pages/main/documentsIn/Rightbar'
import RightbarDR from 'pages/main/documentsReturn/Rightbar'
import FilterSearch from 'pages/parts/FilterSearch'
import FilterPagination from 'pages/parts/FilterPagination'
import PeriodPicker from 'pages/parts/PeriodPicker'
import PageTabs from 'pages/parts/PageTabs'
import HintIcon from 'pages/parts/HintIcon'

import { httpOk, GET, FILE_D } from 'helpers/api'
import { formatDate, formatMoney, returnSign, findKeyFromArrayByValue, PAGE_SIZES } from 'helpers/helpers'
import { businessToday } from 'helpers/businessDay'

// Движение по поставщикам: сводка по каждому поставщику за период и все операции списком.
// Данные — та же история, что в карточке поставщика (/report-suppliers/debts/:id), но сразу по всем.
function Index() {
	const { t } = useTranslation()

	const reduxAccount = useSelector(state => state.account)

	const kinds = [
		{ 'id': 'document_in', 'name': t('document_in') },
		{ 'id': 'return', 'name': t('return') },
		{ 'id': 'payment', 'name': t('payment') },
		{ 'id': 'correction', 'name': t('correction') },
	]

	const date = new Date()

	const [activeTab, setActiveTab] = useState('summary')
	const [summary, setSummary] = useState({})
	const [transactions, setTransactions] = useState({})
	const [poses, setPoses] = useState([])
	const [suppliers, setSuppliers] = useState([])
	const [supplierTypes, setSupplierTypes] = useState([])
	const [selectedItem, setSelectedItem] = useState({})
	const [showRightBar, setShowRightBar] = useState({
		'documents_in': false,
		'documents_return': false,
	})
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [filterData, setFilterData] = useState({
		start_date: formatDate(new Date(date.getFullYear(), date.getMonth(), 1), 'yyyy-MM-dd'),
		end_date: businessToday(),
		pos_id: reduxAccount.pos_id,
		supplier_ids: [],
		supplier_type_id: null,
		kind: null,
		search: '',
		size: 20,
	})
	const [pagination, setPagination] = useState({
		url: '/report-suppliers-transactions',
		size: 20,
		response: null
	})

	function buildParams(currentFilterData, withPaging = true) {
		var urlParams = ""
		if (currentFilterData.pos_id)
			urlParams += returnSign(urlParams) + 'pos_id=' + currentFilterData.pos_id
		if (currentFilterData.start_date)
			urlParams += returnSign(urlParams) + 'start_date=' + currentFilterData.start_date
		if (currentFilterData.end_date)
			urlParams += returnSign(urlParams) + 'end_date=' + currentFilterData.end_date
		if (currentFilterData.supplier_ids?.length > 0)
			urlParams += returnSign(urlParams) + 'supplier_ids=' + currentFilterData.supplier_ids.map(item => item.id).join(',')
		if (currentFilterData.supplier_type_id)
			urlParams += returnSign(urlParams) + 'supplier_type_id=' + currentFilterData.supplier_type_id
		if (currentFilterData.kind)
			urlParams += returnSign(urlParams) + 'kind=' + currentFilterData.kind
		if (currentFilterData.search)
			urlParams += returnSign(urlParams) + 'search=' + encodeURIComponent(currentFilterData.search)
		if (withPaging && currentFilterData.size)
			urlParams += returnSign(urlParams) + 'size=' + currentFilterData.size
		if (withPaging && currentFilterData.page)
			urlParams += returnSign(urlParams) + 'page=' + currentFilterData.page
		return urlParams
	}

	async function searchWithFilter(passedData = {}) {
		const filterDataCopy = { ...filterData, ...(passedData.filterData || {}) }

		const responseSummary = await GET('/report-suppliers-movements' + buildParams(filterDataCopy, false), {}, { loader: true })
		if (httpOk(responseSummary)) setSummary(responseSummary.data)

		await getTransactions(filterDataCopy)

		setFilterData(filterDataCopy)
		setDropdownIsOpen(false)
	}

	async function getTransactions(filterDataCopy) {
		const filterUrl = '/report-suppliers-transactions' + buildParams(filterDataCopy)
		const response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) {
			setTransactions(response.data)
			setPagination({ ...pagination, 'url': filterUrl, 'size': filterDataCopy.size, 'response': response.data })
		}
	}

	async function downloadExcelFile() {
		const url = activeTab === 'summary' ? '/report-suppliers-movements' : '/report-suppliers-transactions'
		var urlParams = buildParams(filterData, false)
		urlParams += returnSign(urlParams) + 'excel=true'

		const responseExcel = await FILE_D(url + urlParams, { loader: true })
		if (httpOk(responseExcel)) {
			const file = new Blob([responseExcel.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
			const fileUrl = URL.createObjectURL(file)
			const link = document.createElement('a')
			link.href = fileUrl
			link.setAttribute('download', activeTab === 'summary' ? 'supplier-movements.xlsx' : 'supplier-transactions.xlsx')
			document.body.appendChild(link)
			link.click()
			URL.revokeObjectURL(fileUrl)
		}
	}

	// Из сводки — к операциям одного поставщика
	function openSupplierTransactions(item) {
		const supplier = suppliers.find(option => option.id === item.supplier_id) || { 'id': item.supplier_id, 'name': item.name }
		setActiveTab('transactions')
		searchWithFilter({ filterData: { ...filterData, supplier_ids: [supplier], page: null } })
	}

	// Документы, которые умеем открывать из операций (как в карточке поставщика)
	const documentEndpoints = {
		'documents_in': '/documents-in',
		'documents_return': '/documents-return',
	}

	function isDocumentRow(item) {
		return !!item.entity_id && !!documentEndpoints[item.entity_type]
	}

	async function toggleRightbar(boolean = false, item = {}) {
		if (!boolean) {
			setSelectedItem({})
			setShowRightBar({ 'documents_in': false, 'documents_return': false })
			return
		}
		if (!isDocumentRow(item)) return

		const response = await GET(`${documentEndpoints[item.entity_type]}/${item.entity_id}`, {}, { loader: true })
		if (httpOk(response)) {
			setSelectedItem(response.data)
			setShowRightBar({
				'documents_in': item.entity_type === 'documents_in',
				'documents_return': item.entity_type === 'documents_return',
			})
		}
	}

	function resetFilter() {
		searchWithFilter({
			filterData: {
				supplier_ids: [],
				supplier_type_id: null,
				kind: null,
				search: '',
				page: null,
			}
		})
	}

	function filterDataExist() {
		return filterData.supplier_ids?.length > 0 || !!filterData.supplier_type_id || !!filterData.kind || !!filterData.search
	}

	async function paginate(page) {
		const nextFilterData = { ...filterData, 'page': page }
		setFilterData(nextFilterData)
		getTransactions(nextFilterData)
	}

	function handlePageSizeChange(size) {
		const nextFilterData = { ...filterData, size, page: null }
		setFilterData(nextFilterData)
		getTransactions(nextFilterData)
	}

	async function getPoses() {
		const response = await GET('/helper/poses')
		if (httpOk(response)) setPoses(response.data)
	}

	async function getSuppliers() {
		const response = await GET('/helper/suppliers')
		if (httpOk(response)) setSuppliers(response.data)
	}

	async function getSupplierTypes() {
		const response = await GET('/helper/supplier-types')
		if (httpOk(response)) setSupplierTypes(response.data)
	}

	function debtClass(value) {
		return Number(value) >= 0 ? 'text-success' : 'text-danger'
	}

	useEffect(() => {
		getPoses()
		getSuppliers()
		getSupplierTypes()
		searchWithFilter()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex">
				<h4 className="vertical-center h-100">
					{t('supplier_movements')}
				</h4>
				<div className="vertical-center ms-2">
					<HintIcon text={t('supplier_movements_hint')} />
				</div>
			</div>

			<div className="card">
				<div className="card-body">

					<div className="d-flex justify-content-between flex-wrap mb-3">
						<div className="d-flex gap-2">
							<FilterSearch
								filterData={filterData}
								setFilterData={setFilterData}
								onSearchChange={(search) => searchWithFilter({ filterData: { ...filterData, search, page: null } })}>
							</FilterSearch>
							<div>
								<Dropdown onToggle={() => setDropdownIsOpen(!dropdownIsOpen)} show={dropdownIsOpen}>
									<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
										{t('filter')}
									</Dropdown.Toggle>

									<Dropdown.Menu className="dropdown-filter">
										<div className="d-flex gap-2">
											<div className="form-group w-100">
												<label>{t('pos')}</label>
												<Select
													options={poses}
													value={poses.find(option => option.id === filterData.pos_id) || ''}
													onChange={(option) => setFilterData({ ...filterData, 'pos_id': option.id })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
											<div className="form-group w-100">
												<label>{t('operation_type')}</label>
												<Select
													isClearable
													options={kinds}
													value={kinds.find(option => option.id === filterData.kind) || ''}
													onChange={(option) => setFilterData({ ...filterData, 'kind': option ? option.id : null })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
										</div>
										<div className="d-flex gap-2">
											<div className="form-group w-100">
												<label>{t('suppliers')}</label>
												<Select
													isMulti
													isClearable
													options={suppliers}
													value={filterData.supplier_ids || []}
													onChange={(options) => setFilterData({ ...filterData, 'supplier_ids': options || [] })}
													placeholder={t('all')}
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
										</div>
										<div className="d-flex gap-2">
											<div className="form-group w-100">
												<label>{t('supplier_types')}</label>
												<Select
													isClearable
													options={supplierTypes}
													value={supplierTypes.find(option => option.id === filterData.supplier_type_id) || ''}
													onChange={(option) => setFilterData({ ...filterData, 'supplier_type_id': option ? option.id : null })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
											<div className="form-group w-100">
												<label>{t('period')}</label>
												<PeriodPicker
													startDate={filterData.start_date}
													endDate={filterData.end_date}
													onChange={(period) => setFilterData({ ...filterData, ...period })}
													withTime={false}
													minDate={new Date(new Date().getFullYear() - 1, 0, 1)}
													maxDate={new Date(new Date().getFullYear(), 11, 31)}
												/>
											</div>
										</div>
										<div className="row mt-3">
											<div className="col-md-12">
												<div className="d-flex justify-content-end gap-2">
													<button className="btn btn-secondary"
														onClick={() => setDropdownIsOpen(false)}>
														{t('cancel')}
													</button>
													<button className="btn btn-primary"
														onClick={() => searchWithFilter({ filterData: { ...filterData, page: null } })}>
														{t('apply')}
													</button>
												</div>
											</div>
										</div>
									</Dropdown.Menu>
								</Dropdown>
							</div>
							<div>
								<button type="button" className="btn btn-outline-primary btn-wide-2"
									onClick={() => downloadExcelFile()}
									title="EXCEL">
									<i className="uil uil-file-download-alt"></i>
								</button>
							</div>
						</div>
					</div>

					<div className="filter-block">
						<div className="filter">
							<strong>{t('filter')}</strong>
							<br />
							<span className="me-2">
								<b>{t('period')}: </b>
								<span>{formatDate(filterData.start_date)}</span>
								<span>|</span>
								<span>{formatDate(filterData.end_date)}</span>
							</span>
							{filterData.pos_id &&
								<span className="me-2">
									<b>{t('pos')}: </b>
									{findKeyFromArrayByValue(poses, 'id', filterData.pos_id, 'name')}
								</span>
							}
							{filterData.kind &&
								<span className="me-2">
									<b>{t('operation_type')}: </b>
									{findKeyFromArrayByValue(kinds, 'id', filterData.kind, 'name')}
								</span>
							}
							{filterData.supplier_ids?.length > 0 &&
								<span className="me-2">
									<b>{t('suppliers')}: </b>
									{filterData.supplier_ids.map(item => item.name).join(', ')}
								</span>
							}
							{filterData.supplier_type_id &&
								<span className="me-2">
									<b>{t('supplier_types')}: </b>
									{findKeyFromArrayByValue(supplierTypes, 'id', filterData.supplier_type_id, 'name')}
								</span>
							}
							{filterData.search &&
								<span className="me-2">
									<b>{t('search')}: </b>
									<span>{filterData.search}</span>
								</span>
							}
						</div>
						{filterDataExist() &&
							<div className="vertical-center">
								<button className="btn btn-outline-dark text-uppercase" onClick={resetFilter}>{t('reset')}</button>
							</div>
						}
					</div>

					<PageTabs
						tabs={[
							{ key: 'summary', label: t('by_suppliers') },
							{ key: 'transactions', label: `${t('all_operations')} (${transactions.total || 0})` },
						]}
						active={activeTab}
						onChange={setActiveTab}
					/>

					{activeTab === 'summary' &&
						<div className="table-responsive">
							<table className="table table-bordered table-hover mb-0">
								<thead>
									<tr>
										<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
										<th style={{ 'width': '22%' }}>{t('supplier')}</th>
										<th className="text-end">{t('debt_start')}</th>
										<th className="text-end">{t('goods_received')}</th>
										<th className="text-end">{t('returns')}</th>
										<th className="text-end">{t('paid')}</th>
										<th className="text-end">{t('corrections')}</th>
										<th className="text-end">{t('debt_end')}</th>
										<th className="text-center">{t('operations_count')}</th>
									</tr>
								</thead>
								<tbody>
									{summary?.data?.map((item, index) => (
										<tr key={index}>
											<td className="text-center">{item.row_number}</td>
											<td className="td-ellipsis" title={item.supplier_type_name || ''}>
												<Link className="text-primary cursor table-first-column-clickable"
													to={`/report-suppliers/debts/${item.supplier_id}`}>
													{item.name}
												</Link>
											</td>
											<td className={`text-end text-nowrap ${debtClass(item.opening_balance)}`}>{formatMoney(item.opening_balance)}</td>
											<td className="text-end text-nowrap">{Number(item.received_amount) !== 0 && formatMoney(item.received_amount)}</td>
											<td className="text-end text-nowrap">{Number(item.returned_amount) !== 0 && formatMoney(item.returned_amount)}</td>
											<td className="text-end text-nowrap text-success">{Number(item.paid_amount) !== 0 && formatMoney(item.paid_amount)}</td>
											<td className="text-end text-nowrap">{Number(item.correction_amount) !== 0 && formatMoney(item.correction_amount)}</td>
											<td className={`text-end text-nowrap ${debtClass(item.closing_balance)}`}>{formatMoney(item.closing_balance)}</td>
											<td className="text-center">
												{item.operations_count > 0 &&
													<span className="text-primary cursor" onClick={() => openSupplierTransactions(item)}>
														{item.operations_count}
													</span>
												}
											</td>
										</tr>
									))}
								</tbody>
								<tfoot>
									<tr className="fw-bold">
										<td colSpan="2" className="text-end">{t('total')}</td>
										<td className={`text-end text-nowrap ${debtClass(summary?.totals?.opening_balance)}`}>{formatMoney(summary?.totals?.opening_balance)}</td>
										<td className="text-end text-nowrap">{formatMoney(summary?.totals?.received_amount)}</td>
										<td className="text-end text-nowrap">{formatMoney(summary?.totals?.returned_amount)}</td>
										<td className="text-end text-nowrap text-success">{formatMoney(summary?.totals?.paid_amount)}</td>
										<td className="text-end text-nowrap">{formatMoney(summary?.totals?.correction_amount)}</td>
										<td className={`text-end text-nowrap ${debtClass(summary?.totals?.closing_balance)}`}>{formatMoney(summary?.totals?.closing_balance)}</td>
										<td className="text-center">{summary?.totals?.operations_count}</td>
									</tr>
								</tfoot>
							</table>
						</div>
					}

					{activeTab === 'transactions' &&
						<>
							<div className="table-responsive">
								<table className="table table-bordered table-hover mb-0">
									<thead>
										<tr>
											<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
											<th style={{ 'width': '10%' }} className="text-center">{t('date')}</th>
											<th style={{ 'width': '20%' }}>{t('supplier')}</th>
											<th style={{ 'width': '10%' }}>{t('operation_type')}</th>
											<th style={{ 'width': '10%' }}>{t('payment')}</th>
											<th style={{ 'width': '30%' }}>{t('note')}</th>
											<th style={{ 'width': '10%' }} className="text-end">{t('debit')}</th>
											<th style={{ 'width': '10%' }} className="text-end">{t('credit')}</th>
										</tr>
									</thead>
									<tbody>
										{transactions?.data?.map((item, index) => (
											<tr key={index} className={isDocumentRow(item) ? 'cursor' : ''}
												onClick={() => toggleRightbar(true, item)}>
												<td className="text-center">{item.row_number}</td>
												<td className="text-center text-nowrap">
													{formatDate(item.created_at, 'dd.MM.yy HH:mm')}
													{item.status === -2 &&
														<span className="badge bg-warning text-dark ms-1">{t('expected')}</span>
													}
												</td>
												<td className="td-ellipsis" title={item.supplier_name}>{item.supplier_name}</td>
												<td className="text-nowrap">{t(item.kind)}</td>
												<td className="td-ellipsis">{item.account_chef_name}</td>
												<td className="td-ellipsis" title={item.note}>{item.note}</td>
												<td className="text-end text-nowrap">
													{item.transaction_type === 'plus' && <span className="text-success">{formatMoney(item.amount)}</span>}
												</td>
												<td className="text-end text-nowrap">
													{item.transaction_type === 'minus' && <span>{formatMoney(item.amount)}</span>}
												</td>
											</tr>
										))}
									</tbody>
									<tfoot>
										<tr className="fw-bold">
											<td colSpan="6" className="text-end">{t('total')}</td>
											<td className="text-end text-nowrap text-success">{formatMoney(transactions.debit_amount)}</td>
											<td className="text-end text-nowrap">{formatMoney(transactions.credit_amount)}</td>
										</tr>
									</tfoot>
								</table>
							</div>

							{pagination.response &&
								<FilterPagination
									pagination={pagination}
									paginate={paginate}
									showPageSizeSelect
									pageSizeOptions={PAGE_SIZES}
									onPageSizeChange={handlePageSizeChange}>
								</FilterPagination>
							}
						</>
					}
				</div>
			</div>

			{showRightBar.documents_in &&
				<RightbarDI
					showRightBar={showRightBar.documents_in}
					setShowRightBar={setShowRightBar}
					data={selectedItem}>
				</RightbarDI>
			}

			{showRightBar.documents_return &&
				<RightbarDR
					showRightBar={showRightBar.documents_return}
					setShowRightBar={setShowRightBar}
					data={selectedItem}>
				</RightbarDR>
			}
		</>
	)
}

export default Index
